import type { ComponentType, ReactNode } from "react"
import { Box, Text } from "@chakra-ui/react"
import type { Connective } from "@hydrologiq/shared/components/alerts"

/** One 40px line in the Condition and Threshold columns. */
export interface ConditionLine<T> {
  key: string
  item: T
  /** Index of the cluster this line belongs to, or null at the top level */
  cluster: number | null
}

/**
 * The lines both columns render, in one order, so each threshold sits beside its clause.
 * Top-level conditions first, then each cluster's.
 */
export function conditionLines<T>(top: T[], clusters: T[][]): ConditionLine<T>[] {
  return [
    ...top.map((item, i) => ({ key: `c${i}`, item, cluster: null })),
    ...clusters.flatMap((items, g) => items.map((item, i) => ({ key: `g${g}c${i}`, item, cluster: g }))),
  ]
}

export const LINE_HEIGHT = 40
export const LINE_GAP = 6
const BRACKET_SPACE = 46
const ACCENT = "#39aed2"
const TINT = "rgba(57, 174, 210, 0.08)"

// A group being dragged into: a clear outline round all its lines, not just a tint
const DROP_ZONE = {
  border: `2px dashed ${ACCENT}`,
  bg: "rgba(57, 174, 210, 0.14)",
}
// A group something just landed in: one pulse of the same outline, fading out; held still
// for people who prefer less motion
const LANDED_PULSE = {
  "@keyframes groupLanded": {
    "0%": { boxShadow: "0 0 0 0 rgba(57, 174, 210, 0.7)", background: "rgba(57, 174, 210, 0.22)", borderColor: ACCENT },
    "100%": { boxShadow: "0 0 0 10px rgba(57, 174, 210, 0)", background: "rgba(57, 174, 210, 0)", borderColor: "rgba(57, 174, 210, 0)" },
  },
  border: `2px solid ${ACCENT}`,
  animation: "groupLanded 1.1s ease-out forwards",
  "@media (prefers-reduced-motion: reduce)": { animation: "none", background: "rgba(57, 174, 210, 0.14)" },
}

// A group about to be ungrouped: outlined in red while Ungroup is hovered
const DANGER = "#d2042d"
const UNGROUP_PREVIEW = {
  border: `2px dashed ${DANGER}`,
  bg: "rgba(210, 4, 45, 0.06)",
}

/** Which grouping is highlighted: a cluster's index, the whole group, or none. */
export type GroupHighlight = number | "root" | null

const connectiveLabel = (connective: Connective) =>
  connective === "AND" ? "All conditions must be met" : "Any condition can be met"

/** The right-hand bracket and connective chip spanning lines `from`..`to`. */
function Bracket({
  from,
  to,
  depth,
  connective,
  highlighted,
  danger = false,
  onToggle,
  onHover,
}: {
  from: number
  to: number
  depth: number
  connective: Connective
  highlighted: boolean
  /** About to be ungrouped: bracket and chip in red */
  danger?: boolean
  /** Editing: the chip flips the group between AND and OR */
  onToggle?: () => void
  /** Editing: hovering the bracket highlights its group */
  onHover?: (hovering: boolean) => void
}) {
  // A one-line group (an empty group's slot) still gets a visible bracket
  const inset = to > from ? 14 : 6
  const top = from * (LINE_HEIGHT + LINE_GAP) + inset
  const height = (to - from) * (LINE_HEIGHT + LINE_GAP) + LINE_HEIGHT - inset * 2
  const colour = danger ? DANGER : highlighted ? ACCENT : "#a9a9a9"
  return (
    <Box
      position="absolute"
      top={`${top}px`}
      h={`${height}px`}
      right={`${depth * BRACKET_SPACE}px`}
      w={`${BRACKET_SPACE}px`}
      pointerEvents={onHover ? "auto" : "none"}
      onMouseEnter={onHover && (() => onHover(true))}
      onMouseLeave={onHover && (() => onHover(false))}
      zIndex={1}
    >
      <Box
        position="absolute"
        top={0}
        bottom={0}
        right="12px"
        w="12px"
        border="1.5px solid"
        borderColor={colour}
        borderLeft="none"
        borderRadius="0 5px 5px 0"
        transition="border-color 0.15s ease"
        pointerEvents="none"
      />
      <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="flex-end" pointerEvents="none">
        <Text
          as={onToggle ? "button" : "span"}
          onClick={onToggle}
          aria-label={onToggle ? `${connectiveLabel(connective)}. Change to ${connective === "AND" ? "OR" : "AND"}` : undefined}
          pointerEvents={onToggle ? "auto" : "none"}
          cursor={onToggle ? "pointer" : undefined}
          fontSize="11px"
          fontWeight="semibold"
          letterSpacing="0.05em"
          color={danger ? DANGER : highlighted ? ACCENT : "#545454"}
          bg="white"
          border="1px solid"
          borderColor={colour}
          borderRadius="3px"
          px="5px"
          py="1px"
          transition="all 0.15s ease"
        >
          {connective}
        </Text>
      </Box>
    </Box>
  )
}

export interface LineContainerProps<T> {
  line: ConditionLine<T>
  highlighted: boolean
  children: ReactNode
}

function DefaultLine<T>({ highlighted, children }: LineContainerProps<T>) {
  return (
    <Box
      h={`${LINE_HEIGHT}px`}
      display="flex"
      alignItems="center"
      gap={2}
      minW={0}
      borderRadius="5px"
      bg={highlighted ? TINT : undefined}
      transition="background-color 0.15s ease"
    >
      {children}
    </Box>
  )
}

/**
 * The Condition column: one line per clause, with a bracket and chip for the whole group
 * and one inside it for each cluster. `renderLine` draws a clause (read or edit). With no
 * connective (a Standard Alert's threshold and time window) the lines get no bracket.
 *
 * Editing passes `onHover` (a hovered bracket highlights it and its lines), `highlight`
 * (also used while a condition is dragged over a group), `dropTarget`, `landed`,
 * `ungroupPreview` and `LineContainer`.
 */
export function ConditionGroup<T>({
  lines,
  connective,
  clusterConnectives,
  renderLine,
  trailing,
  onToggle,
  highlight = null,
  onHover,
  LineContainer = DefaultLine,
  fit = false,
  dropTarget = null,
  landed = null,
  ungroupPreview = null,
  invalidClusters = [],
}: {
  lines: ConditionLine<T>[]
  connective: Connective | null
  clusterConnectives: Connective[]
  renderLine: (line: ConditionLine<T>) => ReactNode
  /** e.g. "Add condition" while editing */
  trailing?: ReactNode
  /** Editing: flip a connective; `null` is the whole group, a number is that cluster */
  onToggle?: (cluster: number | null) => void
  highlight?: GroupHighlight
  onHover?: (target: GroupHighlight) => void
  LineContainer?: ComponentType<LineContainerProps<T>>
  /** Size to the lines' content, so the brackets sit just after it rather than at the far edge */
  fit?: boolean
  /** Editing: the cluster a condition is being dragged over, outlined as a drop zone */
  dropTarget?: number | null
  /** Editing: the cluster a condition just landed in, which pulses once */
  landed?: number | null
  /** Editing: the cluster whose Ungroup is hovered, outlined in red with its bracket */
  ungroupPreview?: number | null
  /** Editing: clusters that can't be saved as they are (too few conditions), bracket in red */
  invalidClusters?: number[]
}) {
  const topItems = lines.filter((l) => l.cluster === null).length + clusterConnectives.length
  const showOuter = connective !== null && topItems > 1
  const clusterRanges = clusterConnectives.map((c, g) => {
    const indexes = lines.flatMap((l, i) => (l.cluster === g ? [i] : []))
    return { connective: c, from: indexes[0], to: indexes[indexes.length - 1] }
  })
  const hasClusterBracket = clusterRanges.some((r) => r.from !== undefined && (onHover ? r.to >= r.from : r.to > r.from))
  const depth = hasClusterBracket ? 2 : showOuter ? 1 : 0
  const isHighlighted = (line: ConditionLine<T>) =>
    highlight === "root" || (highlight !== null && line.cluster === highlight)

  return (
    <Box
      role={connective ? "group" : undefined}
      aria-label={connective ? connectiveLabel(connective) : undefined}
      position="relative"
      display="flex"
      flexDirection="column"
      gap={`${LINE_GAP}px`}
      pr={`${depth * BRACKET_SPACE}px`}
      minW={0}
      w={fit ? "fit-content" : undefined}
    >
      {clusterRanges.map((r, g) =>
        r.from !== undefined && (g === dropTarget || g === landed || g === ungroupPreview) ? (
          <Box
            // Keyed by what it shows, so each landing replays the pulse
            key={`zone-${g}-${g === ungroupPreview ? "ungroup" : g === dropTarget ? "drop" : "landed"}`}
            data-ungroup-preview={g === ungroupPreview || undefined}
            position="absolute"
            top={`${r.from * (LINE_HEIGHT + LINE_GAP) - 5}px`}
            h={`${(r.to - r.from) * (LINE_HEIGHT + LINE_GAP) + LINE_HEIGHT + 10}px`}
            left="-8px"
            // Round the lines and the group's own bracket
            right={`${BRACKET_SPACE - 4}px`}
            borderRadius="8px"
            pointerEvents="none"
            transition="background-color 0.15s ease"
            {...(g === ungroupPreview ? UNGROUP_PREVIEW : g === dropTarget ? DROP_ZONE : { css: LANDED_PULSE })}
          />
        ) : null,
      )}
      {lines.map((line) => (
        <LineContainer key={line.key} line={line} highlighted={isHighlighted(line)}>
          {renderLine(line)}
        </LineContainer>
      ))}
      {showOuter && connective && lines.length > 1 && (
        <Bracket
          from={0}
          to={lines.length - 1}
          depth={0}
          connective={connective}
          highlighted={highlight === "root"}
          onToggle={onToggle && (() => onToggle(null))}
          onHover={onHover && ((on) => onHover(on ? "root" : null))}
        />
      )}
      {clusterRanges.map((r, g) =>
        r.from !== undefined && (r.to > r.from || (onHover && r.to === r.from)) ? (
          <Bracket
            key={g}
            from={r.from}
            to={r.to}
            depth={1}
            connective={r.connective}
            highlighted={highlight === g}
            danger={ungroupPreview === g || invalidClusters.includes(g)}
            onToggle={onToggle && (() => onToggle(g))}
            onHover={onHover && ((on) => onHover(on ? g : null))}
          />
        ) : null,
      )}
      {trailing}
    </Box>
  )
}
